/**
 * 하단 시트 (DESIGN_SPEC 6장: 근거 시트). 위쪽 반경 20, 배경 딤, 240ms 로 올라옵니다.
 * 그림자는 이 시트만 아주 옅게 씁니다.
 *
 * 열고 닫는 것은 부르는 쪽이 정합니다. 근거 시트는 라우트(/chat/:memberKey/evidence/:key)
 * 이므로 onClose 가 뒤로 가기입니다. 그래서 안드로이드 뒤로 버튼도 시트만 닫습니다.
 *
 * 열려 있는 동안 뒤 화면의 스크롤을 막고, Esc 로 닫히고, 첫 포커스를 시트 안에 둡니다.
 */
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

export function BottomSheet({ open, onClose, label, children }) {
    const panelRef = useRef(null);
    // 부르는 쪽이 매번 새 함수를 넘겨도 열 때의 처리(포커스, 스크롤 잠금)를 다시 하지 않습니다.
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        const previouslyFocused = document.activeElement;
        panelRef.current?.focus();

        function handleKeyDown(event) {
            if (event.key === 'Escape') {
                onCloseRef.current();
            }
        }
        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = previousOverflow;
            if (previouslyFocused instanceof HTMLElement) {
                previouslyFocused.focus();
            }
        };
    }, [open]);

    if (!open) {
        return null;
    }

    return createPortal(
        <div className="fixed inset-0 z-50 mx-auto flex max-w-app flex-col bg-dim motion-safe:animate-dim-in">
            <button type="button" aria-label={`${label} 닫기`} onClick={onClose} className="flex-1" tabIndex={-1} />
            <section
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-label={label}
                tabIndex={-1}
                className="flex max-h-[85dvh] flex-col gap-3 overflow-y-auto rounded-t-sheet bg-surface px-5 pt-3 pb-[max(28px,env(safe-area-inset-bottom))] outline-none motion-safe:animate-sheet-up"
            >
                <span aria-hidden="true" className="h-1 w-10 self-center rounded-full bg-border" />
                {children}
            </section>
        </div>,
        document.body
    );
}
